import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { REGISTER } from "@/constants/testIds";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await register(form.email, form.password, form.full_name);
      toast.success("Account created — welcome to Orbital");
      nav("/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#0B0D12] text-[#F4F1EB]">
      <div className="flex-1 flex items-center justify-center p-6 lg:p-16">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <img src="/orbital-logo.png" alt="Orbital" className="w-9 h-9 object-contain" />
            <div className="font-display text-xl font-bold">Orbital</div>
          </Link>

          <div className="text-xs tracking-[0.3em] uppercase text-[#7C5CFF] mb-3">Open an account</div>
          <h1 className="font-display text-3xl font-bold mb-8">Create your wallet</h1>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Full name</label>
              <input
                data-testid={REGISTER.nameInput}
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                required
                minLength={2}
                className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none"
                placeholder="Ada Lovelace"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Email</label>
              <input
                data-testid={REGISTER.emailInput}
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Password</label>
              <input
                data-testid={REGISTER.passwordInput}
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none"
                placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.25em] uppercase text-[#A3A9B8]">Confirm password</label>
              <input
                data-testid={REGISTER.passwordConfirmInput}
                type="password"
                required
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="mt-2 w-full bg-[#0B0D12] border border-[#2A2E38] rounded-sm px-4 py-3 focus:border-[#7C5CFF] focus:outline-none"
              />
            </div>
            <button
              data-testid={REGISTER.submitButton}
              type="submit"
              disabled={busy}
              className="w-full py-3 bg-[#7C5CFF] text-white rounded-sm hover:bg-[#9C82FF] disabled:opacity-60 transition-colors"
            >
              {busy ? "Creating account…" : "Create account"}
            </button>
          </form>

          <p className="mt-6 text-sm text-[#A3A9B8]">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid={REGISTER.loginLink}
              className="text-[#7C5CFF] hover:text-[#9C82FF]"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 relative grain">
        <img
          src="https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGFyY2hpdGVjdHVyZSUyMGRhcmt8ZW58MHx8fHwxNzgxMDQ5NTg5fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-[#0B0D12] via-[#0B0D12]/60 to-transparent"></div>
        <div className="relative z-10 p-16 flex flex-col justify-end">
          <h2 className="font-display text-5xl font-black leading-none max-w-md">
            One account for <span className="text-[#7C5CFF]">crypto, EUR, USD & CAD.</span>
          </h2>
          <p className="mt-4 text-[#A3A9B8] max-w-sm">
            Trade Bitcoin and Ethereum, stake your holdings, and move money to your bank
            in a few clicks.
          </p>
        </div>
      </div>
    </div>
  );
}
